import type { ProductVariant } from '@commerce/types/product'
import CalculatorButton from '@components/paintCalculator/CalculatorButton'
import { useUI } from '@components/ui'
import WYSIWYG from '@components/ui/WYSIWYG'
import useScrollToTop from '@lib/hooks/useScrollToTop'
import convert from 'convert'
import { useTranslation } from 'next-i18next'
import { FC } from 'react'
import GardenPotModalButton from '../GardenPotModalButton'
import ProductOptionByLitre from '../ProductOptions/ProductOptionByLitre'
import { SelectedOptions } from '../helpers'
interface props {
  surfaceArea: number
  options: ProductVariant[]
  currencyCode: string
  selectedOptions: SelectedOptions
  setSelectedOptions: React.Dispatch<React.SetStateAction<SelectedOptions>>
  totalLitres?: number
  isGardenPotPaint?: boolean
  isSamplePotPaint?: boolean
  prismicContent?: any
  numberOfCoats: string
}

const ProductPaintSelection: FC<props> = ({
  surfaceArea,
  options,
  currencyCode,
  selectedOptions,
  setSelectedOptions,
  totalLitres,
  isGardenPotPaint,
  isSamplePotPaint,
  prismicContent,
  numberOfCoats,
}) => {
  const { paintCoverageCalculated, calculatedCoverage, calculatorUnit } =
    useUI()
  const { declarationText } = prismicContent || {}
  const { t } = useTranslation('product')
  const componentRef = useScrollToTop(96)
  const unit = () => {
    switch (calculatorUnit) {
      case 'metres':
        return 'm'
      case 'feet':
        return 'ft'
      default:
        return ''
    }
  }
  return (
    <div ref={componentRef} className="flex h-full flex-col justify-between ">
      <div>
        {!paintCoverageCalculated ? (
          <>
            <div className="body-1 text-center text-ui-dark-grey">
              {t('how-much-paint-do-you-need')}
            </div>

            <div className="mx-auto mt-2 flex justify-center gap-1.5">
              {isGardenPotPaint || isSamplePotPaint ? (
                <>
                  {isGardenPotPaint && (
                    <>
                      <div className="body-2 text-brand-dark-grey">
                        <span>{t('unsure')}?</span>
                      </div>
                      <GardenPotModalButton prismicContent={prismicContent} />
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="body-2 text-brand-dark-grey">
                    <span>{t('unsure')}?</span>
                  </div>
                  <CalculatorButton
                    displayText={t('use-our-calculator').toString()}
                  />
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="body-1 text-center text-ui-dark-grey">
              {`You'll need at least ${calculatedCoverage} ${unit()}² coverage`}
            </div>
            <div className="mx-auto mt-2 flex justify-center gap-1.5">
              <CalculatorButton displayText="Calculate again" />
            </div>
          </>
        )}

        <div className="mt-6 md:mt-10">
          <div className="flex flex-col gap-4">
            {options.map((v) => {
              return (
                <ProductOptionByLitre
                  key={v.id}
                  option={v}
                  currencyCode={currencyCode}
                  selectedOptions={selectedOptions}
                  setSelectedOptions={setSelectedOptions}
                />
              )
            })}
          </div>
        </div>
      </div>
      {!isGardenPotPaint && !isSamplePotPaint && (
        <div className="body-2 mt-4 text-center text-ui-dark-grey">
          {t('your-selection-covers-approximately')}
          <span className="font-bold">
            {calculatorUnit == 'metres' && ` ${surfaceArea * totalLitres!} m² `}
            {calculatorUnit == 'feet' &&
              ` ${Math.floor(
                convert(surfaceArea * totalLitres!, 'm2').to('sq ft')
              )} ft² `}
          </span>
          {t('with')} {numberOfCoats}{' '}
          {numberOfCoats == 'one' ? `${t('coat')}` : `${t('coats')}`}.
        </div>
      )}
      {!isGardenPotPaint && !isSamplePotPaint && declarationText && (
        <div className="body-2 mt-auto pb-4 text-center text-ui-dark-grey">
          <WYSIWYG content={declarationText} />
        </div>
      )}
    </div>
  )
}

export default ProductPaintSelection
